import { Loader2, Mail } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CollapsibleCard } from '@/components/admin/CollapsibleCard';
import { useActivityLog } from '@/hooks/admin/useActivityLog';
import { ActivityLine } from '@/pages/admin/AdminActivity';

/**
 * Communications history — every email sent about this customer (order
 * confirmations, invoices, statements, reminders, receipts, welcomes),
 * who triggered it, and when. Reads the same merged feed as /admin/activity,
 * filtered to this customer + communications.
 */
export default function CommsHistoryPanel({ customerId }: { customerId: string }) {
  const query = useActivityLog({ category: 'comm', customerId, limit: 25 });
  const rows = (query.data?.pages ?? []).flatMap((p) => p.rows);

  return (
    <CollapsibleCard
      title="Communications history"
      icon={<Mail size={14} className="text-brix-primary shrink-0" />}
      storageKey={`comms-${customerId}`}
      defaultOpen={false}
      summary={rows.length === 0 ? 'Nothing sent yet' : `${rows.length} sent`}
      desc={
        <>
          Every email sent to this customer &mdash; what, when, and who sent it. Automated
          sends show as “system”.
        </>
      }
    >

      {query.isLoading ? (
        <div className="py-6 text-center text-sm text-brix-muted">
          <Loader2 size={14} className="animate-spin inline-block mr-2" />
          Loading…
        </div>
      ) : query.error instanceof Error ? (
        <div className="text-xs text-brix-warning">{query.error.message}</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-brix-muted italic">No communications logged yet.</div>
      ) : (
        <div className="divide-y divide-brix-border rounded-lg border border-brix-border overflow-hidden">
          {rows.map((row) => (
            <ActivityLine key={row.key} row={row} hideCustomer />
          ))}
        </div>
      )}

      {query.hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          {query.isFetchingNextPage ? <Loader2 size={12} className="animate-spin" /> : null}
          Show older
        </Button>
      )}
    </CollapsibleCard>
  );
}
